import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { withAuthorization } from '../../../shared/services/auth/with-authorization/with-authorization.component';
import {
  APP_ROUTES,
  CALENDAR_PERMITTED_ROLES,
  DASHBOARD_PERMITTED_ROLES
} from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { UserRole } from '../../../shared/enums/user-role.enum';
import {
  getReservationsDashboardSummary,
  getReservationsDashboardUpdates,
  ReservationsDashboardSummary,
  ReservationsDashboardUpdates
} from '../../shared/services/reservations-dashboard.data';
import { StatsGrid } from './components/stats-grid.component';
import { ReservationsPanel } from './components/reservations-panel.component';
import { DoubleBookings } from '../home/components/double-booking/double-booking.component';

// matches the old dashboard sections, which exclude INVESTOR
const OVERVIEW_PERMITTED_ROLES = DASHBOARD_PERMITTED_ROLES.filter(
  (role) => role !== UserRole.INVESTOR
);

const CalendarCallout = withAuthorization(
  CALENDAR_PERMITTED_ROLES,
  true
)(() => {
  const history = useHistory();

  return (
    <section className="rounded-[12px] border border-primary-100 bg-primary-50 px-6 py-8 text-center">
      <p className="text-[13px] text-neutral-400">
        full calendar for a clearer view of scheduling, reservations, and availability.
      </p>
      <button
        type="button"
        onClick={() => history.push(APP_ROUTES.CALENDAR_MODULE.PATH)}
        className="mt-5 min-h-[44px] cursor-pointer rounded-lg bg-primary-700 px-8 text-[14px] font-gilroy-medium text-white transition-colors hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
      >
        Open Full Calendar
      </button>
    </section>
  );
});

export const OverviewContainer = () => {
  const [summary, setSummary] = useState<ReservationsDashboardSummary | null>(null);
  const [updates, setUpdates] = useState<ReservationsDashboardUpdates | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Promise.all([getReservationsDashboardSummary(), getReservationsDashboardUpdates()])
      .then(([summaryResponse, updatesResponse]) => {
        if (cancelled) {
          return;
        }
        if (isErrorInfo(summaryResponse) || isErrorInfo(updatesResponse)) {
          setHasError(true);
        } else {
          setSummary(summaryResponse);
          setUpdates(updatesResponse);
        }
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="font-gilroy-regular flex min-h-[calc(100vh-45px)] w-full flex-col gap-6 bg-gray-100 px-5 py-8 lg:px-10">
      {hasError ? (
        <section className="rounded-[12px] border border-gray-200 bg-white px-8 py-14 text-center shadow-[0_1px_2px_rgba(24,33,47,0.04)]">
          <p className="text-[16px] font-gilroy-medium text-gray-900">
            Reservation figures could not be loaded.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 min-h-[44px] cursor-pointer rounded-lg bg-primary-700 px-7 text-[14px] font-gilroy-medium text-white transition-colors hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
          >
            Try again
          </button>
        </section>
      ) : (
        <>
          <StatsGrid summary={summary} isLoading={isLoading} />
          <div className="overflow-x-auto rounded-[12px] border border-gray-200 border-l-4 border-l-secondary-600 bg-white shadow-[0_1px_2px_rgba(24,33,47,0.04)]">
            <DoubleBookings />
          </div>
          <ReservationsPanel updates={updates} isLoading={isLoading} />
          <CalendarCallout />
        </>
      )}
    </div>
  );
};

export default connect()(withAuthorization(OVERVIEW_PERMITTED_ROLES, true)(OverviewContainer));
